import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

interface FaultLevel {
  scenario: string;
  ik_3phase: string;
  ik_double_3phase: string;
  ip_3phase: string;
}

interface ArcFlash {
  incident_energy: string;
  ppe_category: string;
  boundary: string;
  working_distance: string;
}

interface Setting {
  name: string;
  existing_value: string;
  recommended_value: string;
  unit: string;
  modified: boolean;
  compliance: string;
  fault_ratio_pct: string;
}

interface InjectionTest {
  last_test_date: string;
  next_due: string;
  result: string;
  company: string;
}

interface Device {
  id: number;
  tag: string;
  type: string;
  manufacturer: string;
  model: string;
  ct_ratio: string;
  tcc_ref: string;
  settings: Setting[];
  injection_test: InjectionTest | null;
  pending_changes: number;
}

interface Bus {
  id: number;
  name: string;
  voltage: string;
  fault_withstand_duration: string;
  fault_withstand_current: string;
  fault_levels: FaultLevel[];
  arc_flash: ArcFlash | null;
  devices: Device[];
}

interface SubstationData {
  id: number;
  name: string;
  type: string;
  voltage_primary: string;
  voltage_secondary: string;
  transformer_rating: string;
  buses: Bus[];
}

const ppeColor = (cat: string) => {
  switch (cat?.toUpperCase()) {
    case 'CAT1': return '#4caf50';
    case 'CAT2': return '#2196f3';
    case 'CAT3': return '#ff9800';
    case 'CAT4': return '#f44336';
    default: return '#888';
  }
};

const SubstationDetail: React.FC = () => {
  const { subId } = useParams<{ subId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<SubstationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/api/substations/${subId}`);
        setData(res.data);
      } catch (err: any) {
        setError('Failed to load substation data.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [subId]);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f5f6fa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <header style={{
        background: '#1a1a2e',
        color: '#fff',
        padding: '0 32px',
        height: 64,
        display: 'flex',
        alignItems: 'center',
        gap: 20,
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        flexShrink: 0,
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: '#fff',
            padding: '7px 16px',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
            fontFamily: 'inherit',
          }}
        >
          ← Back
        </button>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700 }}>
            {data ? data.name : 'Substation Detail'}
          </div>
          <div style={{ fontSize: 11, color: '#8899aa', letterSpacing: 1 }}>SUBSTATION DETAIL</div>
        </div>
      </header>

      <main style={{ flex: 1, padding: 32 }}>
        {loading && (
          <div style={{ textAlign: 'center', color: '#888', padding: 60, fontSize: 15 }}>
            Loading substation data...
          </div>
        )}
        {error && (
          <div style={{
            background: '#fff5f5',
            border: '1px solid #f44336',
            borderRadius: 8,
            color: '#f44336',
            padding: '14px 18px',
            marginBottom: 24,
          }}>
            {error}
          </div>
        )}

        {!loading && data && (
          <>
            {/* Substation Info Card */}
            <div style={{
              background: '#fff',
              borderRadius: 12,
              padding: '24px 28px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
              marginBottom: 28,
              display: 'flex',
              gap: 32,
              flexWrap: 'wrap',
            }}>
              <InfoField label="Name" value={data.name} large />
              <InfoField label="Type" value={data.type} />
              <InfoField label="Primary Voltage" value={data.voltage_primary} />
              <InfoField label="Secondary Voltage" value={data.voltage_secondary} />
              <InfoField label="Transformer Rating" value={data.transformer_rating} />
            </div>

            {/* Buses */}
            {(data.buses || []).map((bus) => (
              <div key={bus.id} style={{
                background: '#fff',
                borderRadius: 12,
                boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                marginBottom: 28,
                overflow: 'hidden',
              }}>
                {/* Bus header */}
                <div style={{
                  background: '#0f3460',
                  color: '#fff',
                  padding: '16px 24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 20,
                  flexWrap: 'wrap',
                }}>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>{bus.name}</div>
                  <InfoChip label="Voltage" value={bus.voltage} />
                  {bus.fault_withstand_current && (
                    <InfoChip label="Fault Withstand" value={`${bus.fault_withstand_current} / ${bus.fault_withstand_duration}`} />
                  )}
                </div>

                <div style={{ padding: 24 }}>
                  {/* Fault Levels */}
                  {bus.fault_levels && bus.fault_levels.length > 0 && (
                    <div style={{ marginBottom: 24 }}>
                      <SectionTitle>Fault Levels</SectionTitle>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ background: '#f5f6fa' }}>
                            {['Scenario', 'Ik 3-phase', 'Ik" 3-phase', 'Ip 3-phase'].map((h) => (
                              <th key={h} style={{
                                padding: '10px 14px',
                                textAlign: 'left',
                                fontWeight: 600,
                                fontSize: 12,
                                color: '#555',
                                borderBottom: '1px solid #e8eaf0',
                              }}>
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {bus.fault_levels.map((fl, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <td style={{ padding: '10px 14px', fontWeight: 600, color: '#333' }}>{fl.scenario}</td>
                              <td style={{ padding: '10px 14px', color: '#555' }}>{fl.ik_3phase}</td>
                              <td style={{ padding: '10px 14px', color: '#555' }}>{fl.ik_double_3phase}</td>
                              <td style={{ padding: '10px 14px', color: '#555' }}>{fl.ip_3phase}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Arc Flash */}
                  {bus.arc_flash && (
                    <div style={{ marginBottom: 24 }}>
                      <SectionTitle>Arc Flash Analysis</SectionTitle>
                      <div style={{
                        background: '#f8f9fc',
                        border: `2px solid ${ppeColor(bus.arc_flash.ppe_category)}44`,
                        borderRadius: 10,
                        padding: '16px 20px',
                        display: 'flex',
                        gap: 32,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                      }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{
                            background: ppeColor(bus.arc_flash.ppe_category),
                            color: '#fff',
                            borderRadius: 8,
                            padding: '8px 20px',
                            fontWeight: 700,
                            fontSize: 16,
                            marginBottom: 4,
                          }}>
                            {bus.arc_flash.ppe_category}
                          </div>
                          <div style={{ fontSize: 11, color: '#888' }}>PPE Category</div>
                        </div>
                        <InfoField label="Incident Energy" value={bus.arc_flash.incident_energy} />
                        <InfoField label="Arc Flash Boundary" value={bus.arc_flash.boundary} />
                        <InfoField label="Working Distance" value={bus.arc_flash.working_distance} />
                      </div>
                    </div>
                  )}

                  {/* Devices */}
                  {(bus.devices || []).map((device) => (
                    <DeviceCard key={device.id} device={device} />
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </main>

      <footer style={{
        background: '#1a1a2e',
        color: '#8899aa',
        textAlign: 'center',
        padding: '14px',
        fontSize: 12,
        flexShrink: 0,
      }}>
        © 2026 JMB Engineering Pty Ltd — Protection Platform
      </footer>
    </div>
  );
};

const InfoField: React.FC<{ label: string; value: string; large?: boolean }> = ({ label, value, large }) => (
  <div>
    <div style={{ fontSize: 11, color: '#999', marginBottom: 3, letterSpacing: 0.5, textTransform: 'uppercase' }}>{label}</div>
    <div style={{ fontSize: large ? 18 : 14, fontWeight: large ? 700 : 500, color: '#1a1a2e' }}>{value || '—'}</div>
  </div>
);

const InfoChip: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{
    background: 'rgba(255,255,255,0.12)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 6,
    padding: '5px 12px',
    fontSize: 12,
  }}>
    <span style={{ opacity: 0.7 }}>{label}: </span>
    <span style={{ fontWeight: 600 }}>{value}</span>
  </div>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    fontSize: 12,
    fontWeight: 700,
    color: '#888',
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: 12,
  }}>
    {children}
  </div>
);

const DeviceCard: React.FC<{ device: Device }> = ({ device }) => (
  <div style={{
    border: '1.5px solid #e8eaf0',
    borderRadius: 10,
    marginBottom: 20,
    overflow: 'hidden',
  }}>
    {/* Device header */}
    <div style={{
      background: '#1a1a2e',
      color: '#fff',
      padding: '12px 18px',
      display: 'flex',
      alignItems: 'center',
      gap: 16,
      flexWrap: 'wrap',
    }}>
      <span style={{ fontWeight: 700, fontSize: 15 }}>{device.tag}</span>
      <span style={{
        background: '#0f3460',
        border: '1px solid rgba(255,255,255,0.2)',
        borderRadius: 4,
        padding: '2px 10px',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 0.5,
      }}>
        {device.type}
      </span>
      <span style={{ fontSize: 13, opacity: 0.8 }}>{device.manufacturer} {device.model}</span>
      {device.ct_ratio && (
        <span style={{ fontSize: 12, opacity: 0.7 }}>CT: {device.ct_ratio}</span>
      )}
      {device.tcc_ref && (
        <span style={{ fontSize: 12, opacity: 0.7 }}>TCC: {device.tcc_ref}</span>
      )}
      {device.pending_changes > 0 && (
        <span style={{
          background: '#ff9800',
          color: '#fff',
          borderRadius: 20,
          padding: '2px 10px',
          fontSize: 11,
          fontWeight: 600,
          marginLeft: 'auto',
        }}>
          {device.pending_changes} pending change{device.pending_changes !== 1 ? 's' : ''}
        </span>
      )}
    </div>

    <div style={{ padding: '16px 18px' }}>
      {/* Settings comparison table */}
      {device.settings && device.settings.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <SectionTitle>Protection Settings</SectionTitle>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#f5f6fa' }}>
                {['Setting Name', 'Existing Value', 'Recommended Value', 'Unit', 'Modified', 'Compliance', 'Fault Ratio %'].map((h) => (
                  <th key={h} style={{
                    padding: '9px 12px',
                    textAlign: 'left',
                    fontWeight: 600,
                    color: '#555',
                    fontSize: 11,
                    letterSpacing: 0.3,
                    borderBottom: '1px solid #e8eaf0',
                    whiteSpace: 'nowrap',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {device.settings.map((s, i) => (
                <tr
                  key={i}
                  style={{
                    background: s.modified ? '#fff5f5' : (i % 2 === 0 ? '#fff' : '#fafbfc'),
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  <td style={{ padding: '9px 12px', fontWeight: 500, color: '#333' }}>{s.name}</td>
                  <td style={{ padding: '9px 12px', color: '#555' }}>{s.existing_value}</td>
                  <td style={{ padding: '9px 12px', color: '#0f3460', fontWeight: 600 }}>{s.recommended_value}</td>
                  <td style={{ padding: '9px 12px', color: '#888' }}>{s.unit}</td>
                  <td style={{ padding: '9px 12px' }}>
                    {s.modified ? (
                      <span style={{
                        background: '#f4433622',
                        color: '#f44336',
                        border: '1px solid #f4433644',
                        borderRadius: 20,
                        padding: '2px 8px',
                        fontSize: 11,
                        fontWeight: 600,
                      }}>Yes</span>
                    ) : (
                      <span style={{ color: '#999', fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '9px 12px' }}>
                    <ComplianceBadge value={s.compliance} />
                  </td>
                  <td style={{ padding: '9px 12px', color: '#555' }}>{s.fault_ratio_pct}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Injection Test */}
      {device.injection_test && (
        <div>
          <SectionTitle>Injection Test Status</SectionTitle>
          <div style={{
            background: '#f8f9fc',
            border: '1px solid #e8eaf0',
            borderRadius: 8,
            padding: '14px 18px',
            display: 'flex',
            gap: 28,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}>
            <div>
              <div style={{ fontSize: 11, color: '#999', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.5 }}>Last Test</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{device.injection_test.last_test_date || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#999', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.5 }}>Next Due</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{device.injection_test.next_due || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#999', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.5 }}>Result</div>
              <span style={{
                background: device.injection_test.result?.toLowerCase() === 'pass' ? '#4caf5022' : '#f4433622',
                color: device.injection_test.result?.toLowerCase() === 'pass' ? '#4caf50' : '#f44336',
                border: `1px solid ${device.injection_test.result?.toLowerCase() === 'pass' ? '#4caf5044' : '#f4433644'}`,
                borderRadius: 20,
                padding: '3px 12px',
                fontSize: 12,
                fontWeight: 700,
                textTransform: 'uppercase',
              }}>
                {device.injection_test.result || '—'}
              </span>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#999', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.5 }}>Testing Company</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{device.injection_test.company || '—'}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  </div>
);

const ComplianceBadge: React.FC<{ value: string }> = ({ value }) => {
  let bg = '#e0e0e022', color = '#888', border = '#e0e0e044';
  if (value?.toLowerCase() === 'compliant') {
    bg = '#4caf5022'; color = '#4caf50'; border = '#4caf5044';
  } else if (value?.toLowerCase() === 'non-compliant') {
    bg = '#f4433622'; color = '#f44336'; border = '#f4433644';
  } else if (value?.toLowerCase() === 'review') {
    bg = '#ff980022'; color = '#ff9800'; border = '#ff980044';
  }
  return (
    <span style={{
      background: bg,
      color,
      border: `1px solid ${border}`,
      borderRadius: 20,
      padding: '2px 9px',
      fontSize: 11,
      fontWeight: 600,
      textTransform: 'capitalize',
    }}>
      {value || '—'}
    </span>
  );
};

export default SubstationDetail;
